/**
 * Serviço Base para APIs - Contém configurações e funcionalidades compartilhadas
 */
import axios from 'axios';
import { configHelper } from '../config/config-helper';

// URLs base para os diferentes serviços (usando as portas _v2)
const rawApiBaseUrl = configHelper.getPropertyServiceUrl();
if (!rawApiBaseUrl) {
  throw new Error(
    '[BaseApiService] Environment variable VITE_PROPERTY_SERVICE_URL is not defined. Please configure it before running the application.',
  );
}
export const API_BASE_URL = rawApiBaseUrl;

export const API_V1_URL = `${API_BASE_URL}/api/v1`;
export const AUTH_SERVICE_URL = configHelper.getAuthServiceUrl();
export const PROPERTY_SERVICE_URL = configHelper.getPropertyServiceUrl();
export const MUNICIPALITIES_SERVICE_URL = configHelper.getMunicipalitiesServiceUrl();
export const HUB_SERVICE_URL = configHelper.getHubServiceUrl();
export const CONSOLIDATE_SERVICE_URL = configHelper.getConsolidateServiceUrl();
// G-18 (web-plataforma-adjustments Wave 5, 2026-05-26): removidos exports
// EMAIL_SERVICE_URL, GEOSPATIAL_SERVICE_URL, TILES_SERVICE_URL e instance
// `emailApi`. Services descartados ou nao consumidos pelo FE.

// Configurações de CORS
export const CORS_ENABLED = import.meta.env.VITE_CORS_ENABLED === 'true';
export const CORS_ORIGINS = import.meta.env.VITE_CORS_ORIGINS?.split(',');
export const CORS_METHODS = import.meta.env.VITE_CORS_METHODS || 'GET,POST,PUT,DELETE,OPTIONS';
export const CORS_ALLOWED_HEADERS =
  import.meta.env.VITE_CORS_ALLOWED_HEADERS || 'Content-Type,Authorization,X-Requested-With';

// Chaves para armazenar dados de autenticação
export const JWT_STORAGE_KEY = 'access_token'; // Usando sessionStorage por padrão
export const JWT_LEGACY_KEY = 'nova_plataforma_jwt_token'; // Compatibilidade localStorage
export const JWT_MODERN_KEY = 'jwt_token'; // Compatibilidade com fluxo novo (src/core/api/client.ts)

// Evento personalizado para notificar sobre mudanças no estado de autenticação
export const AUTH_LOGOUT_EVENT = 'auth:logout';

/**
 * Obtém o token JWT do sessionStorage (preferência) ou localStorage (fallback)
 * @returns {string|null} Token JWT ou null se não existir
 */
export function getJwt() {
  try {
    // Preferir sessionStorage (mais seguro)
    const sessionToken = sessionStorage.getItem(JWT_STORAGE_KEY);
    if (sessionToken) return sessionToken;

    // Fallback para localStorage (compatibilidade)
    const legacyToken = localStorage.getItem(JWT_LEGACY_KEY);
    if (legacyToken) return legacyToken;

    // Fallback para fluxo novo de autenticação
    return localStorage.getItem(JWT_MODERN_KEY);
  } catch (e) {
    console.error('[BaseApiService] Erro ao acessar storage:', e);
    return null;
  }
}

/**
 * Define o token JWT no sessionStorage (principal) e localStorage (backup)
 * @param {string} token - Token JWT
 */
export function setJwt(token) {
  try {
    if (token) {
      // Salvar em sessionStorage (principal)
      sessionStorage.setItem(JWT_STORAGE_KEY, token);
      // Salvar em localStorage (compatibilidade)
      localStorage.setItem(JWT_LEGACY_KEY, token);
      // Salvar no padrão novo
      localStorage.setItem(JWT_MODERN_KEY, token);
    } else {
      // Remover de ambos
      sessionStorage.removeItem(JWT_STORAGE_KEY);
      localStorage.removeItem(JWT_LEGACY_KEY);
      localStorage.removeItem(JWT_MODERN_KEY);
    }
  } catch (e) {
    console.error('[BaseApiService] Erro ao salvar no storage:', e);
  }
}

/**
 * Remove o token JWT e dados do usuário, e dispara evento de logout
 */
export function clearJwt() {
  setJwt(null);

  // Limpar dados do usuário
  try {
    sessionStorage.removeItem('user');
    localStorage.removeItem('user');
  } catch (e) {
    console.error('[BaseApiService] Erro ao limpar dados do usuário:', e);
  }

  // Disparar evento de logout
  try {
    window.dispatchEvent(new Event(AUTH_LOGOUT_EVENT));
  } catch (e) {
    console.error('[BaseApiService] Erro ao disparar evento de logout:', e);
  }
}

/**
 * Parseia o header HTTP `Retry-After` em segundos.
 * Aceita formato delta-seconds (RFC 7231 §7.1.3) ou HTTP-date.
 * @param {string|undefined|null} header - Valor do header Retry-After
 * @returns {number|null} Segundos até o retry permitido (>=0) ou null se inválido
 */
export function parseRetryAfter(header) {
  if (header == null || header === '') return null;
  // delta-seconds (inteiro)
  const asInt = Number(header);
  if (Number.isFinite(asInt) && asInt >= 0) return Math.ceil(asInt);
  // HTTP-date
  const asDate = Date.parse(header);
  if (!Number.isNaN(asDate)) {
    const delta = Math.ceil((asDate - Date.now()) / 1000);
    return delta > 0 ? delta : 0;
  }
  return null;
}

/**
 * Cria uma instância do Axios com configurações comuns
 * @param {string} baseURL - URL base para as requisições
 * @returns {AxiosInstance} Instância configurada do Axios
 */
export function createApi(baseURL) {
  // Criar instância com URL base
  const api = axios.create({
    baseURL,
    timeout: 60000,
  });

  // Configurar interceptadores
  api.interceptors.request.use(
    (config) => {
      // Adicionar token JWT se estiver disponível
      const token = getJwt();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Interceptor para lidar com respostas e erros
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      // Verificar se é erro de autenticação (401)
      if (error.response?.status === 401) {
        console.warn('[BaseApiService] Erro de autenticação 401, limpando token');
        clearJwt();
      }

      // Rate-limit (429): enriquecer erro com retryAfter (s) parseado do header.
      // Backend gn-api-auth-service aplica 5 req/5min em /auth/login (tier=control).
      if (error.response?.status === 429) {
        const retryAfter = parseRetryAfter(error.response.headers?.['retry-after']);
        error.retryAfter = retryAfter;
        console.warn(
          `[BaseApiService] Rate-limit 429${retryAfter ? ` (retry em ~${retryAfter}s)` : ''}`,
        );
      }

      // Tratativa específica para erros de CORS (pode ser customizada conforme necessário)
      if (error.message && error.message.includes('Network Error') && CORS_ENABLED) {
        console.error('[BaseApiService] Possível erro de CORS na requisição:', error);
      }

      return Promise.reject(error);
    },
  );

  return api;
}

// Instâncias de API para os diferentes serviços
// Em DEV: sem base URL, os proxies do Vite roteiam baseado no path completo
// Em PROD: URL completa do serviço
// Autenticação via Bearer token do usuário logado
export const authApi = createApi(import.meta.env.DEV ? '' : AUTH_SERVICE_URL);
// Em DEV, usar proxy do Vite em '/api' (ver vite.config.ts). Em PROD, usar URL explícita.
export const propertyApi = createApi(import.meta.env.DEV ? '' : PROPERTY_SERVICE_URL);
// A API de municípios usa o prefixo /api/v1/ adicionado em cada endpoint do MunicipalitiesService.
export const municipalitiesApi = createApi(import.meta.env.DEV ? '' : MUNICIPALITIES_SERVICE_URL);

/**
 * Formata mensagens de erro para exibição ao usuário
 * @param {Error} error - Objeto de erro
 * @returns {string} Mensagem formatada
 */
export function formatErrorMessage(error) {
  if (error.response?.status === 429) {
    const retryAfter = error.retryAfter ?? parseRetryAfter(error.response.headers?.['retry-after']);
    if (retryAfter && retryAfter > 0) {
      return `Muitas tentativas. Aguarde ${retryAfter}s e tente novamente.`;
    }
    return 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.';
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.response?.data?.error) {
    return error.response.data.error;
  } else if (error.message) {
    return error.message;
  }
  return 'Ocorreu um erro inesperado';
}
